import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Repeat } from "lucide-react";

const KIND_LABEL = {
  lezione: "Lezione",
  riposo: "Giornata di riposo",
  uscita_missionaria: "Uscita missionaria",
  viaggio_missionario: "Viaggio missionario",
  esame: "Esame",
  pranzo: "Pranzo",
  preghiera: "Preghiera",
  altro: "Altro",
};

const REC_LABEL = {
  none: "Nessuna",
  daily: "Ogni giorno",
  weekly: "Ogni settimana",
  monthly: "Ogni mese",
};

const WEEK_DAYS = [
  { v: 0, label: "Lun" }, { v: 1, label: "Mar" }, { v: 2, label: "Mer" },
  { v: 3, label: "Gio" }, { v: 4, label: "Ven" }, { v: 5, label: "Sab" }, { v: 6, label: "Dom" },
];

const DEFAULT_ACADEMIC_YEAR = "2026/2027";

const EMPTY = {
  date: "", start_time: "", end_time: "", kind: "lezione",
  title: "", description: "", course_id: "", teacher_ids: [],
  year_levels: [1, 2, 3], track_types: ["residenziale"],
  academic_year: DEFAULT_ACADEMIC_YEAR,
  location: "", all_day: false,
  recurrence: "none", recurrence_days: [], recurrence_end_date: "",
};

export default function EventFormDialog({ open, onOpenChange, mode = "create", initial, onSubmit, submitting, defaultDate }) {
  const [form, setForm] = useState(EMPTY);
  const { data: courses = [] } = useQuery({ queryKey: ["courses-event"], queryFn: async () => (await api.get("/courses")).data, enabled: open });
  const { data: teachers = [] } = useQuery({ queryKey: ["teachers-event"], queryFn: async () => (await api.get("/teachers")).data, enabled: open });

  useEffect(() => {
    if (open) setForm(initial ? {
      ...EMPTY, ...initial,
      teacher_ids: initial.teacher_ids || [],
      year_levels: initial.year_levels?.length ? initial.year_levels : [1, 2, 3],
      track_types: initial.track_types?.length ? initial.track_types : ["residenziale"],
      academic_year: initial.academic_year || DEFAULT_ACADEMIC_YEAR,
      // in edit mode non ripropone la creazione di nuove ricorrenze
      recurrence: "none", recurrence_days: [], recurrence_end_date: "",
    } : { ...EMPTY, date: defaultDate || "" });
  }, [open, initial, defaultDate]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleArr = (key, val) => setForm((p) => ({
    ...p, [key]: p[key].includes(val) ? p[key].filter((x) => x !== val) : [...p[key], val],
  }));

  function submit(e) {
    e.preventDefault();
    const p = { ...form };
    ["start_time", "end_time", "description", "location", "course_id"].forEach((k) => { if (!p[k]) p[k] = null; });
    if (mode === "edit") {
      // in edit non inviamo campi di ricorrenza (gestiti solo in create)
      delete p.recurrence; delete p.recurrence_days; delete p.recurrence_end_date;
    } else if (p.recurrence === "none") {
      p.recurrence_days = null;
      p.recurrence_end_date = p.recurrence_end_date || null;
    }
    onSubmit(p);
  }

  const isEdit = mode === "edit";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica evento" : "Nuovo evento"}</DialogTitle>
          <DialogDescription>
            Assegna docenti, materie, uscite missionarie, giornate di riposo, pranzi e attività ricorrenti al calendario didattico.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tipo evento</Label>
              <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                <SelectTrigger data-testid="event-kind"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(KIND_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Anno accademico</Label>
              <Input
                data-testid="event-academic-year"
                value={form.academic_year}
                onChange={(e) => set("academic_year", e.target.value)}
                placeholder="2026/2027"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Data{isEdit ? "" : " di inizio"}</Label>
            <Input type="date" required data-testid="event-date" value={form.date} onChange={(e) => set("date", e.target.value)}/>
          </div>
          <div className="flex items-end gap-2">
            <Checkbox id="allday" checked={form.all_day} onCheckedChange={(v) => set("all_day", !!v)} data-testid="event-allday"/>
            <Label htmlFor="allday" className="text-sm cursor-pointer mb-1.5">Tutto il giorno</Label>
          </div>
          {!form.all_day && (
            <>
              <div>
                <Label className="text-xs">Dalle</Label>
                <Input type="time" value={form.start_time || ""} onChange={(e) => set("start_time", e.target.value)}/>
              </div>
              <div>
                <Label className="text-xs">Alle</Label>
                <Input type="time" value={form.end_time || ""} onChange={(e) => set("end_time", e.target.value)}/>
              </div>
            </>
          )}
          <div className="col-span-2">
            <Label className="text-xs">Titolo</Label>
            <Input required data-testid="event-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={form.kind === "lezione" ? "Sistematica 1 - Modulo A" : form.kind === "pranzo" ? "Pranzo" : "Titolo evento"}/>
          </div>
          {form.kind === "lezione" && (
            <>
              <div>
                <Label className="text-xs">Corso</Label>
                <Select value={form.course_id || "__none"} onValueChange={(v) => set("course_id", v === "__none" ? "" : v)}>
                  <SelectTrigger data-testid="event-course"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Nessuno</SelectItem>
                    {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Aula / luogo</Label>
                <Input value={form.location || ""} onChange={(e) => set("location", e.target.value)}/>
              </div>
            </>
          )}
          {form.kind !== "lezione" && (
            <div className="col-span-2">
              <Label className="text-xs">Luogo / destinazione</Label>
              <Input value={form.location || ""} onChange={(e) => set("location", e.target.value)}/>
            </div>
          )}
          <div className="col-span-2">
            <Label className="text-xs">Docenti coinvolti</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto p-2 border border-stone-200 rounded-md">
              {teachers.length === 0 ? <span className="text-xs text-stone-500">Nessun docente</span> : teachers.map((t) => {
                const active = form.teacher_ids.includes(t.id);
                return (
                  <button key={t.id} type="button" onClick={() => toggleArr("teacher_ids", t.id)}
                    className={`px-2.5 py-1 rounded-full text-xs border ${active ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200"}`}>
                    {t.first_name} {t.last_name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Anni interessati</Label>
              <div className="flex gap-1.5 mt-1.5">
                {[1, 2, 3].map((y) => (
                  <button key={y} type="button" onClick={() => toggleArr("year_levels", y)}
                    className={`flex-1 px-3 py-1.5 rounded-md text-xs border ${form.year_levels.includes(y) ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200"}`}>
                    {y}° anno
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-stone-500 mt-1">Vuoto = tutti gli anni</div>
            </div>
            <div>
              <Label className="text-xs">Percorsi</Label>
              <div className="flex gap-1.5 mt-1.5">
                {["residenziale", "modulare"].map((t) => (
                  <button key={t} type="button" onClick={() => toggleArr("track_types", t)}
                    className={`flex-1 px-3 py-1.5 rounded-md text-xs border capitalize ${form.track_types.includes(t) ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200"}`}>
                    {t}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-stone-500 mt-1">Vuoto = tutti i percorsi</div>
            </div>
          </div>

          {!isEdit && (
            <div className="col-span-2 rounded-lg border border-stone-200 p-3 bg-[#FDFBF7]">
              <div className="flex items-center gap-2 text-xs font-medium text-[#1C352D] mb-2">
                <Repeat size={13}/> Ricorrenza
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Ripeti</Label>
                  <Select value={form.recurrence} onValueChange={(v) => set("recurrence", v)}>
                    <SelectTrigger data-testid="event-recurrence"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {Object.entries(REC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.recurrence !== "none" && (
                  <div>
                    <Label className="text-xs">Fino al</Label>
                    <Input
                      type="date"
                      data-testid="event-recurrence-end"
                      value={form.recurrence_end_date || ""}
                      onChange={(e) => set("recurrence_end_date", e.target.value)}
                    />
                    <div className="text-[10px] text-stone-500 mt-1">Se vuoto: fine A.A. ({form.academic_year})</div>
                  </div>
                )}
              </div>
              {form.recurrence === "weekly" && (
                <div className="mt-3">
                  <Label className="text-xs">Giorni della settimana</Label>
                  <div className="flex gap-1 mt-1.5">
                    {WEEK_DAYS.map((d) => (
                      <button
                        key={d.v} type="button"
                        onClick={() => toggleArr("recurrence_days", d.v)}
                        data-testid={`event-rec-day-${d.v}`}
                        className={`flex-1 px-2 py-1.5 rounded-md text-[11px] border ${form.recurrence_days.includes(d.v) ? "bg-[#E2725B] text-white border-[#E2725B]" : "bg-white border-stone-200"}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  <div className="text-[10px] text-stone-500 mt-1">Vuoto = usa il giorno della data di inizio</div>
                </div>
              )}
              {form.recurrence !== "none" && (
                <Badge className="mt-3 bg-[#1C352D] text-white text-[10px]">
                  Verranno create più occorrenze in un&apos;unica serie
                </Badge>
              )}
            </div>
          )}

          <div className="col-span-2">
            <Label className="text-xs">Descrizione / note</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)}/>
          </div>
          <DialogFooter className="col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={submitting} data-testid="event-submit" className="bg-[#1C352D] hover:bg-[#264A3E]">
              {submitting ? "Salvataggio…" : isEdit ? "Salva" : "Aggiungi evento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
